/**
 * PaneBody — how a component knows it is rendered INSIDE a pane.
 *
 * A `Panel` already draws the chrome: a glass box, a header with the pane's
 * title and icon, a close button, its own scroll region. A component dropped
 * into one that also draws a card and a header produces a card in a card and
 * two stacked headers — which reads as a bug, because it is one.
 *
 * Every pane body used to solve this with a prop of its own: `hideHeader` on
 * the audit log, `bare` on the milestones list, and the next one would have
 * invented a third name. They all answer the same question, and it is a
 * question about WHERE the component is, not about what the caller wants — so
 * it is context, set once by `Panel`, and read by whoever cares:
 *
 *     const inPane = useInPane();   // true anywhere under a Panel's body
 *
 * The old props still work and still WIN when passed, because a few call
 * sites use them outside a pane (the global side panel renders the audit log
 * headerless too). Context is the default; the prop is the override.
 *
 * `PaneBody` is the wrapper for a pane body that is rendered somewhere other
 * than a `Panel` — a preview, a test harness, a sheet built by hand — and for
 * bodies that want the standard full-height flex column in one place rather
 * than repeating `flex flex-col h-full min-h-0` per pane.
 */

import React, { createContext, useContext } from 'react';
import clsx from 'clsx';
import Skeleton from '../TailwindComponents/Skeleton';

const InPaneContext = createContext(false);

/** True when this component is being rendered inside a surface pane. */
export const useInPane = (): boolean => useContext(InPaneContext);

/**
 * Marks a subtree as pane content WITHOUT adding an element.
 *
 * `Panel` wraps its children in this, so nothing had to change in any pane
 * body's markup for `useInPane()` to start answering correctly.
 */
export const PaneBodyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <InPaneContext.Provider value>{children}</InPaneContext.Provider>
);

/**
 * The standard pane-body box: a full-height flex column that lets its own
 * content own the scrolling, plus the in-pane flag.
 *
 * `isLoading` is the same bargain `Table` makes: the shell owns the pending
 * state, so a pane body never has to decide between a spinner, a blank box
 * and a bespoke skeleton. Pass the flag and the pane-shaped skeleton appears
 * in place of the children.
 */
const PaneBody: React.FC<{
  className?: string;
  /** First-load state — renders the pane-shaped skeleton instead of children. */
  isLoading?: boolean;
  children: React.ReactNode;
}> = ({ className, isLoading = false, children }) => (
  <PaneBodyProvider>
    <div className={clsx('flex flex-col h-full min-h-0', className)}>
      {isLoading ? <Skeleton variant="pane" /> : children}
    </div>
  </PaneBodyProvider>
);

export default PaneBody;
